import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import Badge from "../../components/ui-components/Badge.jsx";
import {
  HeaderPortal,
  useSetHeaderRecordName,
} from "../../components/ui-components/HeaderSlot.jsx";
import Skeleton from "../../components/ui-components/Skeleton";
import TextWithLineBreaks from "../../components/ui-components/TextWithLineBreaks.jsx";
import { authenticatedGet } from "../../api/authenticatedFetch";
import moment from "moment";
import { TYPE_LABELS, resolveActiveStage } from "./logStageMatch";
import PipelineStageStrip from "./PipelineStageStrip";
import { getPipeline } from "./api";

function calculateDuration(startTime, endTime) {
  if (!endTime || endTime === "1900-01-01T00:00:00Z") return "—";
  return moment.utc(moment(endTime).diff(moment(startTime))).format("HH:mm:ss");
}

const StatusCard = ({ label, children, subtext }) => (
  <div className="bg-card rounded-card border border-card-bd p-4">
    <div className="text-[11px] font-medium uppercase tracking-wider text-text-2 mb-2">{label}</div>
    {children}
    {subtext && <div className="text-xs text-text-2 mt-1">{subtext}</div>}
  </div>
);

const DataImportsLogDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const { data, isLoading, isError } = useQuery({
    queryKey: ["log-detail", id],
    queryFn: async () => {
      const json = await authenticatedGet(`/get-logging/${id}`);
      return json.Log || {};
    },
  });

  // Feed the breadcrumb tail, so it reads Settings / Activity Log / <this entry> the way
  // /budgets/:id reads Plan / Budgets / <budget>. Hooks cannot sit behind the loading
  // early-returns below, so this is called unconditionally and passes null until the row
  // arrives — the crumb falls back to "Activity" meanwhile.
  useSetHeaderRecordName(data?.action || null);

  // The log row names its job type but not its stage; fetch the pipeline so the strip
  // can render the real stage list and resolve the active one by name.
  const pipelineId = data?.pipeline_id;
  const { data: pipelineData } = useQuery({
    queryKey: ["pipeline", pipelineId],
    queryFn: () => getPipeline(pipelineId),
    enabled: !!pipelineId,
  });

  if (isLoading) {
    return (
      <div className="p-8">
        <Skeleton height={64} className="mb-8" />
        <Skeleton height={200} />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="p-8 text-center">
        <h2 className="text-xl font-semibold text-text mb-2">Log entry not found</h2>
        <button
          onClick={() => navigate(-1)}
          className="text-primary hover:text-primary-hover cursor-pointer text-sm"
        >
          Back to Activity Log
        </button>
      </div>
    );
  }

  const typeLabel = TYPE_LABELS[data.job_type] || data.category || "System";
  const hasPipeline = !!data.pipeline_id;
  const duration = calculateDuration(data.date_time, data.end_date_time);

  return (
    <div className="h-full flex flex-col bg-background-grey">
      {/* Identity goes into the shared header's row-1 title slot, matching /budgets/:id:
          same typography, status beside the name, and the back chevron + breadcrumb come
          from TopBar because headerTabs marks this route a detail screen. */}
      <HeaderPortal slot="title">
        <div className="flex items-center gap-3 min-w-0">
          <div className="text-[20px] leading-[1.4] font-extrabold tracking-[-0.015em] text-text truncate font-['Plus_Jakarta_Sans',sans-serif]">
            {data.action || "Activity"}
          </div>
          {data.status && <Badge status={data.status} />}
        </div>
      </HeaderPortal>

      <div className="p-8 flex-1 overflow-auto">
        {/* Info line */}
        <div className="text-sm text-text-2 mb-6">
          {typeLabel}
          {data.host && data.host !== "To be determined" ? ` · ${data.host}` : ""}
          {hasPipeline ? " · Pipeline" : ""}
        </div>

        {/* Status Cards */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <StatusCard label="Status">
            <Badge status={data.status} />
          </StatusCard>
          <StatusCard label="Started">
            <div className="text-base font-semibold text-text">
              {data.date_time ? moment(data.date_time).format("DD-MM-YYYY HH:mm:ss") : "—"}
            </div>
          </StatusCard>
          <StatusCard label="Duration">
            <div className="text-base font-semibold text-text">{duration}</div>
          </StatusCard>
          {hasPipeline ? (
            <StatusCard label="Source">
              <button
                onClick={() => navigate("/data-pipeline")}
                className="text-base font-semibold text-primary hover:text-primary-hover cursor-pointer text-left"
              >
                Pipeline › {typeLabel}
              </button>
            </StatusCard>
          ) : data.schedule_id ? (
            <StatusCard label="Source">
              <button
                onClick={() => navigate(data.job_type === "report_generation" ? `/reporting-jobs/${data.schedule_id}` : `/data-pipeline/${data.schedule_id}`)}
                className="text-base font-semibold text-primary hover:text-primary-hover cursor-pointer text-left"
              >
                {data.job_type === "report_generation" ? "Reporting Job" : `Schedule #${data.schedule_id}`}
              </button>
            </StatusCard>
          ) : (
            <StatusCard label="Type">
              <div className="text-base font-semibold text-text">{typeLabel}</div>
            </StatusCard>
          )}
        </div>

        {/* Pipeline stage indicator */}
        {hasPipeline && (
          <div className="bg-card rounded-card border border-card-bd overflow-hidden mb-6">
            <div className="px-5 py-3 border-b border-border">
              <span className="text-base font-semibold text-text">Pipeline Stage</span>
            </div>
            <div className="p-5">
              <PipelineStageStrip
                stages={pipelineData?.stages}
                activeStage={resolveActiveStage(pipelineData?.stages, data.job_type)}
              />
            </div>
          </div>
        )}

        {/* Details */}
        <div className="bg-card rounded-card border border-card-bd overflow-hidden mb-6">
          <div className="px-5 py-3 border-b border-border">
            <span className="text-base font-semibold text-text">Details</span>
          </div>
          <div className="p-5">
            {data.details ? (
              <div className="text-sm text-text font-mono whitespace-pre-wrap break-all">
                <TextWithLineBreaks text={data.details} />
              </div>
            ) : (
              <p className="text-sm text-text-2">No details available.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataImportsLogDetail;
